import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import Portal from "components/Portal";
import ProfileAvatar from "components/__reusable/ProfileAvatar";
import SecondaryActionButton from "components/__reusable/SecondaryActionButton";
import PrimaryActionButton from "components/__reusable/PrimaryActionButton";
import PictureThumbnail from "components/__reusable/PictureThumbnail";
import PublishPictureAdjustmentDialog from "./PublishPictureAdjustmentDialog";
import ErrorIcon from "assets/icons/ErrorIcon";
import LoadingIcon from "assets/icons/LoadingIcon";
import { HiOutlineLightBulb } from "react-icons/hi";
import useAppDispatch from "hooks/useAppDispatch";
import useAppSelector from "hooks/useAppSelector";
import { formatDate } from "utilities/DateFormatter";
import { PicturePublishStatus, SupportedLang } from "config/constants";
import { RiShareForwardFill } from "react-icons/ri";
import {
  getPublishablePicture,
  getUnpublishablePicture,
} from "utilities/PictureStatusFilter";
import type {
  PictureGroupByStudent,
  PublishPictureFormData,
} from "types/Pictures";
import {
  getPictureGroupByStudent,
  publishPicture,
  updateHashTagUsedTime,
} from "services/pictureServices";
import { setErrorStatus } from "features/error/errorSlice";
import { updatePublishStatus } from "features/picture/pictureSlice";
import { setSuccessStatus } from "features/success/successSlice";

interface PublishPicturePromptDialogProps {
  onClose: () => void;
}

const PublishPicturePromptDialog: React.FC<PublishPicturePromptDialogProps> = ({
  onClose,
}) => {
  const { t } = useTranslation();
  const { id } = useParams();

  const { pictures } = useAppSelector((state) => state.picture);
  const [totalIndividualPictures, setTotalIndividualPictures] = useState(0);
  const [totalClassPictures, setTotalClassPictures] = useState(0);
  const [pictureGroupByStudent, setPictureByStudent] = useState<
    PictureGroupByStudent[]
  >([]);
  const [selectedPictureGroup, setSelectedPictureGroup] = useState<
    PictureGroupByStudent | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const fetchInfo = useCallback(async () => {
    setIsLoading(true);
    const publishablePictures = getPublishablePicture(pictures);
    if (publishablePictures.length === 0) return;
    const individualPictures = publishablePictures.filter(
      (picture) => picture.picture_type !== 2
    );
    const classPictures = publishablePictures.filter(
      (picture) => picture.picture_type === 2
    );
    setTotalIndividualPictures(individualPictures.length);
    setTotalClassPictures(classPictures.length);

    try {
      const pictureGroupByStudent = await getPictureGroupByStudent(
        parseInt(id || "")
      );
      setPictureByStudent(pictureGroupByStudent);
      // Update selectedPictureGroup using the current state value
      setSelectedPictureGroup((current) => {
        if (current) {
          return pictureGroupByStudent.find(
            (student) => student.id === current.id
          );
        }
        return current;
      });
    } catch (e) {
    } finally {
      setIsLoading(false);
    }
  }, [pictures, id]);

  useEffect(() => {
    fetchInfo();
  }, [fetchInfo]);

  const renderStudentName = (student: PictureGroupByStudent) => {
    return `${student.last_name} ${student.first_name}`;
  };

  const dispatch = useAppDispatch();
  const publish = async () => {
    setIsLoading(true);
    const publishablePictures = getPublishablePicture(pictures);
    const unpublishablePictures = getUnpublishablePicture(pictures);
    try {
      const formData: PublishPictureFormData = {
        picture_ids: publishablePictures.map((p) => p.id),
        not_good_picture_ids: unpublishablePictures.map((p) => p.id),
      };
      await publishPicture(formData);
      const hashTagMap: { [key: string]: boolean } = {};
      for (let picture of publishablePictures) {
        dispatch(
          updatePublishStatus({
            id: picture.id,
            publishStatus: PicturePublishStatus.published,
          })
        );
        for (let tag of picture.hashtags_info) {
          if (!hashTagMap[tag.id]) hashTagMap[tag.id] = true;
        }
      }
      for (let picture of unpublishablePictures) {
        dispatch(
          updatePublishStatus({
            id: picture.id,
            publishStatus: PicturePublishStatus.deleted,
          })
        );
      }
      const hashTagId: number[] = [];
      for (let id of Object.keys(hashTagMap)) {
        hashTagId.push(parseInt(id));
      }
      await updateHashTagUsedTime(hashTagId);
      dispatch(
        setSuccessStatus({
          title: "_unpublished_picture_class._success_published",
          message: "_unpublished_picture_class._success_published_content",
          isPublished: true,
        })
      );
      onClose();
    } catch (e: any) {
      dispatch(
        setErrorStatus(
          e.response?.data?.error ||
            e.response?.data?.detail ||
            "_generic._unknown_error"
        )
      );
    } finally {
      setIsLoading(false);
    }
  };

  const [isOpenAdjustmentDialog, setIsOpenAdjustmentDialog] = useState(false);

  const getTotalUnpublishablePictures = () => {
    const unpublishablePictures = getUnpublishablePicture(pictures);
    const uniqueIds = new Set(unpublishablePictures.map((p) => p.id));
    return uniqueIds.size;
  };

  return (
    <Portal>
      <div className="fixed top-0 left-0 w-full h-full bg-black/50 flex justify-center items-center z-[70] p-2 sm:p-4">
        <div className="w-full max-w-2xl max-h-[95vh] p-3 sm:p-5 bg-white z-[71] relative rounded-lg flex flex-col">
          <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-center mb-3">
            {t("_unpublished_picture_class._publication_confirmation")}
          </h2>

          <div className="space-y-2 sm:space-y-0 sm:relative mb-3">
            <div className="sm:float-left">
              <p className="text-sm sm:text-base break-words sm:whitespace-nowrap">
                {formatDate(new Date(), {
                  [SupportedLang.ja]: "yo Mo do (EEEEE) HH:mm",
                  [SupportedLang.en]: "EEEE, MMM d, yyyy, HH:mm",
                })}
              </p>
              <p className="text-sm sm:text-base break-words sm:whitespace-nowrap">
                <span className="font-semibold">
                  {t("_unpublished_picture_class._delivered_pictures")}
                </span>
                {`${totalIndividualPictures + totalClassPictures} ${t(
                  "_unpublished_picture_class._images"
                )}`}
              </p>
            </div>
            <div className="flex items-center justify-center sm:absolute sm:bottom-0 sm:left-0 sm:right-0">
              <ErrorIcon width={24} height={24} color={"#ff0000"} />
              <p className="text-sm sm:text-base ml-1">{`${t(
                "_unpublished_picture_class._unpublishable_picture"
              )}: ${getTotalUnpublishablePictures()}${t(
                "_unpublished_picture_class._images"
              )}`}</p>
            </div>
            <div className="absolute bottom-0 right-0 z-20">
              <div className="relative group">
                <button
                  type="button"
                  className="focus:outline-none flex items-center text-[#e6b941]"
                  aria-label="Hint"
                >
                  <span className="mr-0.5 text-xs sm:text-sm">
                    {t("_unpublished_picture_class._hint")}
                  </span>
                  <HiOutlineLightBulb
                    size={20}
                    color="#e6b941"
                    className="inline-block sm:w-6 sm:h-6"
                  />
                </button>
                <div className="absolute right-0 top-full mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50">
                  <div className="relative bg-gray-800 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap min-w-max max-w-xs">
                    {t("_unpublished_picture_class._hint_content")}
                    <div className="absolute bottom-full right-4 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-800"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto relative">
            <div className="space-y-3 pb-4">
              {pictureGroupByStudent.map((student) => (
                <li
                  key={student.id}
                  className="w-full flex flex-col sm:flex-row items-start space-y-2 sm:space-y-0 sm:space-x-3"
                >
                  <div
                    id="student-info"
                    className="w-full sm:w-2/5 lg:w-1/3 flex items-center space-x-2 min-w-0"
                  >
                    <div className="relative flex-shrink-0">
                      <ProfileAvatar
                        pictureUrl={student.profile_picture_for_admin || ""}
                      />
                      <div className="w-5 h-5 sm:w-6 sm:h-6 absolute -bottom-1 -right-1 rounded-full text-white text-xs bg-[#e6b941] flex justify-center items-center font-semibold z-10">
                        {student.student_pictures.length}
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm sm:text-base break-words leading-tight">
                        {renderStudentName(student)}
                      </p>
                    </div>
                  </div>

                  <div className="w-full sm:w-3/5 lg:w-2/3 overflow-x-auto">
                    <div className="grid grid-cols-6 gap-1 sm:gap-2 pb-1">
                      {student.student_pictures.map((picture) => (
                        <button
                          key={picture.id}
                          className="aspect-square w-full min-w-[40px]"
                          onClick={() => {
                            if (picture.picture.thumbnail_picture) {
                              setIsOpenAdjustmentDialog(true);
                              setSelectedPictureGroup(student);
                            }
                          }}
                        >
                          <PictureThumbnail
                            imageSrc={picture.picture.thumbnail_picture}
                            name="picture"
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                </li>
              ))}

              {(() => {
                const publishablePictures = getPublishablePicture(pictures);
                const classPictures = publishablePictures.filter(
                  (picture) => picture.picture_type === 2
                );

                if (classPictures.length > 0) {
                  return (
                    <li className="w-full flex flex-col sm:flex-row items-start space-y-2 sm:space-y-0 sm:space-x-3">
                      <div className="w-full sm:w-2/5 lg:w-1/3 flex items-center space-x-2 min-w-0">
                        <div className="relative flex-shrink-0">
                          <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-lg font-bold text-gray-700">
                            全
                          </div>
                          <div className="w-5 h-5 sm:w-6 sm:h-6 absolute -bottom-1 -right-1 rounded-full text-white text-xs bg-[#e6b941] flex justify-center items-center font-semibold z-10">
                            {classPictures.length}
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm sm:text-base break-words leading-tight">
                            クラス全体
                          </p>
                        </div>
                      </div>

                      <div className="w-full sm:w-3/5 lg:w-2/3">
                        <div className="max-h-48 overflow-y-auto">
                          <div className="grid grid-cols-6 gap-1 sm:gap-2">
                            {classPictures.map((picture) => (
                              <button
                                key={picture.id}
                                className="aspect-square w-full min-w-[40px]"
                                onClick={() => {
                                  if (picture.thumbnail_picture) {
                                    setIsOpenAdjustmentDialog(true);
                                    setSelectedPictureGroup({
                                      id: -1,
                                      student_pictures: classPictures.map(
                                        (p) => ({ id: p.id, picture: p })
                                      ),
                                      first_name: "クラス全体",
                                      last_name: "",
                                      first_name_kata: "",
                                      last_name_kata: "",
                                      profile_picture_for_admin: "",
                                    });
                                  }
                                }}
                              >
                                <PictureThumbnail
                                  imageSrc={picture.thumbnail_picture}
                                  name="class-picture"
                                />
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                }
                return null;
              })()}
            </div>
          </div>

          <div className="mt-3 pt-2 border-t border-gray-200">
            {!isLoading ? (
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                <div className="w-full sm:w-1/2">
                  <SecondaryActionButton
                    label={t("_generic._cancel")}
                    onClick={onClose}
                  />
                </div>
                <div className="w-full sm:w-1/2">
                  <PrimaryActionButton
                    icon={<RiShareForwardFill />}
                    label={t("_unpublished_picture_class._publish_button")}
                    onClick={publish}
                  />
                </div>
              </div>
            ) : (
              <div className="w-12 sm:w-16 lg:w-20 mx-auto">
                <LoadingIcon />
              </div>
            )}
          </div>

          {isOpenAdjustmentDialog && (
            <PublishPictureAdjustmentDialog
              pictureGroupByStudent={selectedPictureGroup}
              onClose={() => setIsOpenAdjustmentDialog(false)}
            />
          )}
        </div>
      </div>
    </Portal>
  );
};

export default PublishPicturePromptDialog;
